/// Types needed in the components for rendering the charts:

import { JsThemeColors } from '@/core/services/theme.service';

export type BarDataType = {
    labels: string[];
    datasets: BarChartVM[];
};

export type PieDataType = {
    labels: string[];
    datasets: [
        {
            data: number[];
            backgroundColor: string[];
            borderWidth?: number;
            borderColor?: string;
            // borderRadius?: number;
            spacing?: number;
            // offset?: number;
            // hoverOffset?: number;
        },
    ];
};

/// Original Chart VM data types

export interface ChartModelDetail {
    color: string;
    tileSum: number;
    name: string;
}

export interface ChartModel {
    date: string;
    details: ChartModelDetail[];
}

export interface ChartVM {
    totalSum: number; // total time in seconds
    highestName: string;
    dates: string[];
    barChart: BarChartVM[];
    pieChart: PieChartVM;
    tableData: ChartTableData[];
}

export interface BarChartVM {
    label: string;
    backgroundColor: string;
    borderWidth: number;
    // borderRadius: number;
    borderColor: string;
    borderSkipped: boolean;
    borderRadiusOnAllStackedSeries: boolean;
    data: number[];
}

export interface PieChartVM {
    labels: string[];
    data: number[];
    backgroundColor: string[];
}

export interface ChartTableData {
    group1: string;
    group2: string;
    durationSeconds: number;
    backgroundColor: string;
}

// TODO remove the ChartVM classes completely be refactoring the dashboard to use the new report API
// Note for this reason the old legacy any usage is not fixed but ignored...

export class ChartOptions {
    static stackedOptions = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                callbacks: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (tooltipItem: any, _index: any, _tooltipItems: any, _data: any): string | undefined => {
                        let label = tooltipItem.dataset.label;
                        const value = tooltipItem.formattedValue;

                        const separator = (1.1).toLocaleString().substring(1, 2);

                        const hours = value.split(separator)[0];

                        let mins = +('0.' + value.split(separator)[1]);
                        mins = mins ? mins * 60 : 0;
                        const min = mins === 0 ? '00' : Math.round(mins);

                        const time = hours + 'h ' + (min.toString().length === 1 ? '0' + min : min) + 'm';
                        label += ': ' + time;

                        // label += ': ' + hours + 'h ' + (mins === 0 ? '00' : Math.round(mins)) + 'm';

                        return time === '0h 00m' ? undefined : label;
                    },
                },
            },
        },

        responsive: true,

        scales: {
            x: {
                stacked: true,
            },
            y: {
                stacked: true,
                ticks: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    callback: function (value: any, _index: number, _ticks: number): string {
                        const n = new Date(0, 0);

                        n.setMinutes(+value * 60);
                        const result = n.toTimeString().slice(0, 5);

                        if (+value >= 24) {
                            const twentyFourCounter = 24 * Math.floor(+value / 24);
                            const hours = result.split(':')[0];
                            const mins = result.split(':')[1];
                            return `${+hours + twentyFourCounter}:${mins}`;
                        }
                        return result;
                    },
                },
            },
        },
    };
    static stackedOptionsWithLegend = addLegend(ChartOptions.stackedOptions);
    static pieOptions = {
        plugins: {
            legend: {
                position: 'bottom',
                align: 'middle',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 12,
                    boxHeight: 12,
                    color: '#ffffff',
                },
            },
            tooltip: {
                callbacks: {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    label: (tooltipItem: any, _index: any, _tooltipItems: any, _data: any): string => {
                        let label = tooltipItem.label;
                        const value = tooltipItem.formattedValue;

                        const separator = (1.1).toLocaleString().substring(1, 2);

                        const hours = value.split(separator)[0];
                        let mins = +('0.' + value.split(separator)[1]);
                        mins = mins ? mins * 60 : 0;

                        const min = mins === 0 ? '00' : Math.round(mins);
                        label += ': ' + hours + 'h ' + (min.toString().length === 1 ? '0' + min : min) + 'm';
                        // label += ': ' + hours + 'h ' + (mins === 0 ? '00' : Math.round(mins)) + 'm';

                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const totalSum = tooltipItem.chart.data.datasets[0].data.reduce((a: any, b: any) => a + b, 0);
                        const percentValue: number = tooltipItem.parsed / totalSum;
                        const percent = (percentValue * 100).toFixed(2);

                        return label + ` (=${percent}%)`;
                    },
                },
            },
        },
        responsive: true,
        maintainAspectRatio: true,
        cutout: 50,
        borderColor: '#bbb',
        offset: 0,
        spacing: 5,
    };
}

export type BarChartIntervalOptions = 'DAY' | 'WEEK' | 'MONTH' | 'QUARTER' | 'YEAR';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function addLegend(orgChartOptions: any): any {
    return {
        ...orgChartOptions,
        plugins: {
            ...orgChartOptions.plugins,
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'circle',
                    boxWidth: 12,
                    boxHeight: 12,
                    color: '#ffffff',
                    padding: 15,
                },
            },
        },
    };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function chartApplyColors(chartOptions: any, colors: JsThemeColors, withoutScales = false): void {
    chartOptions.plugins = chartOptions.plugins || {};
    chartOptions.plugins.legend = chartOptions.plugins.legend || {};
    chartOptions.plugins.legend.labels = chartOptions.plugins.legend.labels || {};
    chartOptions.plugins.legend.labels.color = colors.chartLabelColor;
    if (chartOptions.scales && !withoutScales) {
        chartOptions.scales.x = chartOptions.scales.x || {};
        chartOptions.scales.x.ticks = chartOptions.scales.x.ticks || {};
        chartOptions.scales.x.ticks.color = colors.chartAxisLabelColor;
        chartOptions.scales.y = chartOptions.scales.y || {};
        chartOptions.scales.y.ticks = chartOptions.scales.y.ticks || {};
        chartOptions.scales.y.ticks.color = colors.chartAxisLabelColor;
    }
}

export function applyThemeColorsToCharts(colors: JsThemeColors): void {
    chartApplyColors(ChartOptions.stackedOptions, colors);
    chartApplyColors(ChartOptions.stackedOptionsWithLegend, colors);
    chartApplyColors(ChartOptions.pieOptions, colors, true);
}
